import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Network, AlertTriangle, Search, ChevronDown, ChevronRight, ChevronsUpDown } from 'lucide-react';
import { api } from '../context/AuthContext';

export { Network as VpnLogIcon };

const RANGES = [
  { h: 1, label: '1 hour' }, { h: 6, label: '6 hours' }, { h: 24, label: '24 hours' },
  { h: 72, label: '3 days' }, { h: 168, label: '7 days' }, { h: 720, label: '30 days' },
];

// Classify a VPN log line so the important events stand out.
// DPD request/ACK and raw packet lines are routine heartbeats (every ~10 s).
const NOISE_RE = /DPD request|DPD ACK|sending packet|received packet/i;
function classify(ev) {
  const s = String(ev.details || '');
  // A Phase 2 DELETE right after a new SA is established is a normal rekey,
  // so DELETE is shown as Negotiation, not Problem.
  if (/timed out|timeout|fail|mismatch|no response|down|error|retransmit|AUTHENTICATION|NO_PROPOSAL/i.test(s)) return 'bad';
  if (/established/i.test(s)) return 'good';
  if (/DELETE/i.test(s)) return 'warn';
  if (NOISE_RE.test(s)) return 'info';
  if (/Main Mode|Quick Mode|Phase|proposal|pre-shared|rekey|negotiat|initiat|match/i.test(s)) return 'warn';
  return 'info';
}

const BADGE = {
  bad: 'bg-red-50 text-red-700 border-red-200',
  warn: 'bg-amber-50 text-amber-700 border-amber-200',
  good: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  info: 'bg-slate-50 text-slate-600 border-slate-200',
};
const LABEL = { bad: 'Problem', warn: 'Negotiation', good: 'Established', info: 'Heartbeat / Info' };

// Natural IP compare (10.2 < 10.10) for sorting the Tunnel column.
const ipKey = (s) => String(s || '').split('.').map((p) => String(p).padStart(3, '0')).join('.');
const KIND_ORDER = { bad: 0, warn: 1, good: 2, info: 3 };
const SORT_COLS = [
  ['time', 'Time'], ['kind', 'Type'], ['tunnel', 'Tunnel'],
  ['phase1', 'IKE P1'], ['phase2', 'IKE P2'], ['details', 'Details'],
];

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch (_) { return iso; }
};


// ---------------------------------------------------------------------------
// VPN-TRAFFIC (24 Sep 2026): traffic per tunnel dari metrik CloudWatch AWS/VPN.
// In = site -> AWS (TunnelDataIn), Out = AWS -> site (TunnelDataOut).
const C_IN = '#2a78d6';
const C_OUT = '#eb6834';
const fmtBytes = (b) => {
  const n = Number(b) || 0;
  if (n >= 1e12) return (n / 1e12).toFixed(2) + ' TB';
  if (n >= 1e9) return (n / 1e9).toFixed(2) + ' GB';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + ' MB';
  if (n >= 1e3) return (n / 1e3).toFixed(0) + ' KB';
  return n.toFixed(0) + ' B';
};
const fmtMbps = (v) => (v >= 10 ? v.toFixed(0) : v >= 1 ? v.toFixed(1) : v.toFixed(2)) + ' Mbps';
const TR_COLS = [
  ['name', 'VPN · Tunnel', 'l'], ['ip', 'Tunnel IP', 'l'], ['inBytes', 'In (site → AWS)', 'r'],
  ['outBytes', 'Out (AWS → site)', 'r'], ['total', 'Total', 'r'], ['peakInMbps', 'Peak in', 'r'],
  ['peakOutMbps', 'Peak out', 'r'], ['uptimePct', 'Uptime', 'r'],
];

function TrafficChart({ series, period }) {
  const [hover, setHover] = useState(null);
  const W = 800, H = 220, L = 56, R = 12, T = 12, B = 28;
  if (!series || series.length < 2) {
    return <div className="text-xs text-slate-400 py-6 text-center">No traffic datapoints for this tunnel in the selected range.</div>;
  }
  const t0 = new Date(series[0].t).getTime();
  const t1 = new Date(series[series.length - 1].t).getTime();
  const max = Math.max(0.01, ...series.map((p) => Math.max(p.in || 0, p.out || 0)));
  const nice = (() => { const e = Math.pow(10, Math.floor(Math.log10(max))); const m = max / e; return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * e; })();
  const x = (t) => L + ((new Date(t).getTime() - t0) / Math.max(1, t1 - t0)) * (W - L - R);
  const y = (v) => T + (1 - (v || 0) / nice) * (H - T - B);
  const path = (k) => series.map((p, i) => (i ? 'L' : 'M') + x(p.t).toFixed(1) + ',' + y(p[k]).toFixed(1)).join(' ');
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * nice);
  const xt = [0, 0.25, 0.5, 0.75, 1].map((f) => t0 + f * (t1 - t0));
  const multiDay = t1 - t0 > 36 * 3600 * 1000;
  const fmtX = (t) => new Date(t).toLocaleString('en-GB', multiDay ? { day: '2-digit', month: 'short' } : { hour: '2-digit', minute: '2-digit', hour12: false });
  const onMove = (e) => {
    const box = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - box.left) / box.width) * W;
    let best = 0, bd = Infinity;
    series.forEach((p, i) => { const d = Math.abs(x(p.t) - px); if (d < bd) { bd = d; best = i; } });
    setHover(best);
  };
  const hp = hover != null ? series[hover] : null;
  const last = series[series.length - 1];
  return (
    <div className="relative">
      <div className="flex items-center gap-4 text-xs text-slate-600 mb-1">
        <span className="inline-flex items-center gap-1.5"><span style={{ width: 14, height: 2, background: C_IN, display: 'inline-block', borderRadius: 2 }} />In (site → AWS)</span>
        <span className="inline-flex items-center gap-1.5"><span style={{ width: 14, height: 2, background: C_OUT, display: 'inline-block', borderRadius: 2 }} />Out (AWS → site)</span>
        <span className="text-slate-400">Mbps · average per {period >= 3600 ? 'hour' : '5 min'}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" onMouseMove={onMove} onMouseLeave={() => setHover(null)} role="img" aria-label="Tunnel traffic in and out, Mbps">
        {ticks.map((v) => (
          <g key={v}>
            <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="#e2e8f0" strokeWidth="1" />
            <text x={L - 6} y={y(v) + 3} textAnchor="end" fontSize="10" fill="#94a3b8">{v >= 1 || v === 0 ? +v.toFixed(1) : v.toFixed(2)}</text>
          </g>
        ))}
        {xt.map((t, i) => (
          <text key={i} x={x(t)} y={H - 8} textAnchor={i === 0 ? 'start' : i === 4 ? 'end' : 'middle'} fontSize="10" fill="#94a3b8">{fmtX(t)}</text>
        ))}
        <path d={path('in')} fill="none" stroke={C_IN} strokeWidth="2" strokeLinejoin="round" />
        <path d={path('out')} fill="none" stroke={C_OUT} strokeWidth="2" strokeLinejoin="round" />
        {!hp && <text x={W - R} y={y(last.in) - 6} textAnchor="end" fontSize="10" fill="#475569">in {fmtMbps(last.in || 0)}</text>}
        {hp && (
          <g>
            <line x1={x(hp.t)} x2={x(hp.t)} y1={T} y2={H - B} stroke="#94a3b8" strokeWidth="1" strokeDasharray="3 3" />
            <circle cx={x(hp.t)} cy={y(hp.in)} r="4" fill={C_IN} stroke="#fff" strokeWidth="2" />
            <circle cx={x(hp.t)} cy={y(hp.out)} r="4" fill={C_OUT} stroke="#fff" strokeWidth="2" />
          </g>
        )}
      </svg>
      {hp && (
        <div className="absolute top-6 right-2 bg-white/95 border border-slate-200 rounded-lg shadow-sm px-3 py-2 text-xs text-slate-700 pointer-events-none">
          <div className="font-medium text-slate-800 mb-1">{fmtTime(hp.t)}</div>
          <div className="flex items-center gap-1.5"><span style={{ width: 8, height: 8, background: C_IN, borderRadius: 9, display: 'inline-block' }} />In {fmtMbps(hp.in || 0)}</div>
          <div className="flex items-center gap-1.5"><span style={{ width: 8, height: 8, background: C_OUT, borderRadius: 9, display: 'inline-block' }} />Out {fmtMbps(hp.out || 0)}</div>
          <div className="text-slate-500 mt-0.5">State: {hp.up == null ? '–' : hp.up >= 0.5 ? 'UP' : 'DOWN'}</div>
        </div>
      )}
    </div>
  );
}

function VpnTrafficPanel({ hours, tunnel, onPickTunnel }) {
  const [sum, setSum] = useState(null);
  const [series, setSeries] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openP, setOpenP] = useState(true);
  const [sc, setSc] = useState('total');
  const [sd, setSd] = useState('desc');
  const hrs = Math.min(hours, 24 * 60);

  useEffect(() => {
    let dead = false;
    setBusy(true); setErr(null);
    api.get('/api/aws-cloudtrail/vpn-traffic', { params: { hours: hrs } })
      .then((r) => { if (!dead) setSum(r.data); })
      .catch((e) => { if (!dead) setErr(e.response?.data?.error || e.message); })
      .finally(() => { if (!dead) setBusy(false); });
    return () => { dead = true; };
  }, [hrs]);

  useEffect(() => {
    let dead = false;
    setSeries(null);
    if (tunnel === '__all__') return undefined;
    api.get('/api/aws-cloudtrail/vpn-traffic', { params: { hours: hrs, tunnel } })
      .then((r) => { if (!dead) setSeries(r.data); })
      .catch(() => {});
    return () => { dead = true; };
  }, [hrs, tunnel]);

  const rows = useMemo(() => {
    const list = (sum?.tunnels || []).map((t) => ({ ...t, total: (t.inBytes || 0) + (t.outBytes || 0), name: (t.vpn || '~') + ' ' + (t.idx || '') }));
    return list.sort((a, b) => {
      const va = a[sc], vb = b[sc];
      const cmp = typeof va === 'number' || typeof vb === 'number'
        ? (va ?? -1) - (vb ?? -1)
        : String(va || '').localeCompare(String(vb || ''), undefined, { numeric: true, sensitivity: 'base' });
      return sd === 'asc' ? cmp : -cmp;
    });
  }, [sum, sc, sd]);
  const tot = rows.reduce((a, r) => ({ in: a.in + (r.inBytes || 0), out: a.out + (r.outBytes || 0) }), { in: 0, out: 0 });
  const toggle = (c) => { if (sc === c) setSd((d) => (d === 'asc' ? 'desc' : 'asc')); else { setSc(c); setSd(c === 'name' || c === 'ip' ? 'asc' : 'desc'); } };
  const sel = series?.tunnels?.[0];

  return (
    <div className="mx-4 mt-3 border border-slate-200 rounded-xl">
      <button onClick={() => setOpenP(!openP)} className="w-full flex items-center gap-2 px-3 py-2 text-left">
        {openP ? <ChevronDown style={{ width: 14, height: 14 }} /> : <ChevronRight style={{ width: 14, height: 14 }} />}
        <span className="font-semibold text-slate-800 text-sm">Traffic per tunnel</span>
        <span className="text-xs text-slate-400">CloudWatch AWS/VPN · last {hrs >= 24 ? (hrs / 24) + ' day' + (hrs > 24 ? 's' : '') : hrs + ' h'}{sum ? ' · ' + sum.region : ''}</span>
        {sum && <span className="ml-auto text-xs text-slate-600">In {fmtBytes(tot.in)} · Out {fmtBytes(tot.out)}</span>}
        {busy && <RefreshCw style={{ width: 12, height: 12 }} className="animate-spin text-slate-400" />}
      </button>
      {openP && (
        <div className="px-3 pb-3">
          {err && <div className="p-2 rounded bg-red-50 text-red-700 text-xs mb-2">{err}</div>}
          {sel && (
            <div className="mb-3 p-3 rounded-lg bg-slate-50">
              <div className="text-xs text-slate-600 mb-1">
                <span className="font-semibold text-slate-800">{sel.vpn ? sel.vpn + ' · T' + sel.idx : sel.ip}</span> <span className="font-mono">{sel.ip}</span>
                {' '}· In {fmtBytes(sel.inBytes)} · Out {fmtBytes(sel.outBytes)}
              </div>
              <TrafficChart series={sel.series} period={series.period} />
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 border-b border-slate-200">
                  {TR_COLS.map(([c, lbl, al]) => (
                    <th key={c} onClick={() => toggle(c)} className={`px-2 py-1.5 font-semibold cursor-pointer select-none whitespace-nowrap hover:text-slate-800 ${al === 'r' ? 'text-right' : 'text-left'}`}>
                      <span className="inline-flex items-center gap-1">{lbl}
                        {sc === c ? <span className="text-blue-600">{sd === 'asc' ? '▲' : '▼'}</span> : <ChevronsUpDown style={{ width: 10, height: 10 }} className="opacity-50" />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!busy && rows.length === 0 && <tr><td colSpan={8} className="px-2 py-4 text-center text-slate-400">No VPN tunnels found in AWS Inventory for this account.</td></tr>}
                {rows.map((r) => (
                  <tr key={r.ip} onClick={() => onPickTunnel(tunnel === r.ip ? '__all__' : r.ip)}
                    className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${tunnel === r.ip ? 'bg-blue-50' : ''}`}>
                    <td className="px-2 py-1.5 text-slate-800">{r.vpn ? r.vpn + ' · T' + r.idx : '–'}</td>
                    <td className="px-2 py-1.5 font-mono">{r.ip}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtBytes(r.inBytes)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtBytes(r.outBytes)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-medium">{fmtBytes(r.total)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtMbps(r.peakInMbps || 0)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtMbps(r.peakOutMbps || 0)}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums ${r.uptimePct != null && r.uptimePct < 99 ? 'text-red-600 font-medium' : ''}`}>{r.uptimePct == null ? '–' : r.uptimePct.toFixed(r.uptimePct >= 99.95 || r.uptimePct === 0 ? 0 : 1) + '%'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-slate-400 mt-1">Click a row to filter the log and show its chart. Peak = highest {sum?.period >= 3600 ? 'hourly' : '5-minute'} average. Uptime = share of periods with TunnelState UP.</div>
        </div>
      )}
    </div>
  );
}

export default function VpnTunnelLog() {
  const [groups, setGroups] = useState([]);
  const [logGroup, setLogGroup] = useState('');
  const [hours, setHours] = useState(24);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('__all__');
  const [tunnel, setTunnel] = useState('__all__');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [hideNoise, setHideNoise] = useState(true);
  const [sortCol, setSortCol] = useState('time');
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir(col === 'time' ? 'desc' : 'asc'); }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/aws-cloudtrail/vpn-log-groups');
        const g = res.data.groups || [];
        setGroups(g);
        if (g.length) setLogGroup(g[0].name);
      } catch (e) {
        setError(e.response?.data?.error || e.message);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!logGroup) return;
    setLoading(true); setError(null);
    try {
      const res = await api.get('/api/aws-cloudtrail/vpn-logs', { params: { logGroup, hours, q: q || undefined } });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }, [logGroup, hours]);

  useEffect(() => { load(); }, [load]);

  const labels = data?.tunnelLabels || {};
  const tunnelName = (ip) => { const l = labels[ip]; return l ? `${l.vpn} · T${l.idx}` : ''; };
  const rows = useMemo(() => {
    const list = (data?.events || []).map((e) => ({ ...e, kind: classify(e) }));
    return list.filter((e) => (!hideNoise || kind === 'info' || !NOISE_RE.test(String(e.details || '')))
      && (kind === '__all__' || e.kind === kind)
      && (tunnel === '__all__' || e.tunnelIp === tunnel || e.stream === tunnel))
      .sort((a, b) => {
        const lb = (data?.tunnelLabels) || {};
        const val = (e) => {
          if (sortCol === 'kind') return String(KIND_ORDER[e.kind] ?? 9);
          if (sortCol === 'tunnel') { const l = lb[e.tunnelIp]; return (l ? l.vpn + ' ' + l.idx + ' ' : '~ ') + ipKey(e.tunnelIp || e.stream); }
          return String(e[sortCol] || '');
        };
        let cmp = val(a).localeCompare(val(b), undefined, { numeric: true, sensitivity: 'base' });
        if (cmp === 0 && sortCol !== 'time') cmp = String(b.time).localeCompare(String(a.time)) * (sortDir === 'asc' ? 1 : -1);
        return sortDir === 'asc' ? cmp : -cmp;
      });
  }, [data, kind, tunnel, hideNoise, sortCol, sortDir]);

  const tunnels = useMemo(() => {
    const s = new Set();
    (data?.events || []).forEach((e) => s.add(e.tunnelIp || e.stream));
    const lb = data?.tunnelLabels || {};
    return [...s].filter(Boolean).sort((a, b) => {
      const la = lb[a], lbb = lb[b];
      if (la && lbb) return (la.vpn + la.idx).localeCompare(lbb.vpn + lbb.idx, undefined, { numeric: true });
      if (la) return -1; if (lbb) return 1;
      return ipKey(a).localeCompare(ipKey(b));
    });
  }, [data]);

  const counts = useMemo(() => {
    const c = { bad: 0, warn: 0, good: 0, info: 0 };
    (data?.events || []).forEach((e) => { c[classify(e)] += 1; });
    return c;
  }, [data]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
      <div className="p-4 border-b border-slate-100 flex flex-wrap items-center gap-2">
        <Network style={{ width: 18, height: 18 }} className="text-blue-600" />
        <h3 className="font-semibold text-slate-800 mr-2">VPN Tunnel Log</h3>
        <select value={logGroup} onChange={(e) => setLogGroup(e.target.value)}
          className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
          {groups.length === 0 && <option value="">No /aws/vpn log group</option>}
          {groups.map((g) => <option key={g.name} value={g.name}>{g.name}</option>)}
        </select>
        <select value={hours} onChange={(e) => setHours(Number(e.target.value))}
          className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
          {RANGES.map((r) => <option key={r.h} value={r.h}>Last {r.label}</option>)}
        </select>
        <select value={tunnel} onChange={(e) => setTunnel(e.target.value)}
          className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
          <option value="__all__">All tunnels</option>
          {tunnels.map((t) => <option key={t} value={t}>{tunnelName(t) ? `${tunnelName(t)} — ${t}` : t}</option>)}
        </select>
        <div className="relative">
          <Search style={{ width: 14, height: 14 }} className="absolute left-2 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            placeholder="Search text (Enter)"
            className="text-sm border border-slate-200 rounded-lg pl-7 pr-2 py-1.5 w-52" />
        </div>
        <button onClick={load} disabled={loading || !logGroup}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white disabled:opacity-50">
          <RefreshCw style={{ width: 14, height: 14 }} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="px-4 pt-3 flex flex-wrap gap-2 text-xs">
        {[['__all__', `All (${data?.events?.length || 0})`], ['bad', `Problem (${counts.bad})`],
          ['warn', `Negotiation (${counts.warn})`], ['good', `Established (${counts.good})`], ['info', `Heartbeat / Info (${counts.info})`]]
          .map(([k, l]) => (
            <button key={k} onClick={() => setKind(k)}
              className={`px-2.5 py-1 rounded-full border ${kind === k ? 'bg-blue-600 text-white border-blue-600' : (BADGE[k] || 'bg-white text-slate-600 border-slate-200')}`}>
              {l}
            </button>
          ))}
        <label className="inline-flex items-center gap-1.5 text-slate-600 ml-2">
          <input type="checkbox" checked={hideNoise} onChange={(e) => setHideNoise(e.target.checked)} />
          Hide DPD heartbeats &amp; packets
        </label>
        {data && <span className="ml-auto text-slate-400 self-center">
          {data.region} · {fmtTime(data.start)} – {fmtTime(data.end)}{data.truncated ? (data.coveredFrom ? ` · all IKE/IPsec events shown; DPD heartbeats & packets only since ${fmtTime(data.coveredFrom)}` : ' · truncated, narrow the range') : ''}
        </span>}
      </div>

      <VpnTrafficPanel hours={hours} tunnel={tunnel} onPickTunnel={setTunnel} />

      {error && (
        <div className="m-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm flex gap-2 items-start">
          <AlertTriangle style={{ width: 16, height: 16 }} className="mt-0.5 shrink-0" /> {error}
        </div>
      )}

      <div className="p-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-800 text-white text-xs uppercase">
              <th className="px-3 py-2 text-left w-6"></th>
              {SORT_COLS.map(([c, lbl]) => (
                <th key={c} onClick={() => toggleSort(c)}
                  className="px-3 py-2 text-left cursor-pointer select-none hover:bg-slate-700 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1">{lbl}
                    {sortCol === c
                      ? <span className="text-blue-300">{sortDir === 'asc' ? '▲' : '▼'}</span>
                      : <ChevronsUpDown style={{ width: 10, height: 10 }} className="text-slate-400 opacity-60" />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">
                No log entries in this range. VPN tunnel logs only record IKE/IPsec/DPD activity, so a quiet tunnel has few entries.
              </td></tr>
            )}
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <tr onClick={() => setOpen(open === r.id ? null : r.id)}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <td className="px-3 py-2 text-slate-400">
                    {open === r.id ? <ChevronDown style={{ width: 14, height: 14 }} /> : <ChevronRight style={{ width: 14, height: 14 }} />}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-700">{fmtTime(r.time)}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full border text-xs ${BADGE[r.kind]}`}>{LABEL[r.kind]}</span></td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap"><div className="font-mono">{r.tunnelIp || r.stream}</div>{tunnelName(r.tunnelIp) && <div className="text-[11px] text-slate-500">{tunnelName(r.tunnelIp)}</div>}</td>
                  <td className="px-3 py-2 text-xs">{r.phase1 || '–'}</td>
                  <td className="px-3 py-2 text-xs">{r.phase2 || '–'}</td>
                  <td className="px-3 py-2 text-slate-600">{r.details || '–'}</td>
                </tr>
                {open === r.id && (
                  <tr className="bg-slate-50"><td colSpan={7} className="px-3 py-2">
                    <pre className="text-xs whitespace-pre-wrap break-all text-slate-700">
                      {typeof r.raw === 'string' ? r.raw : JSON.stringify(r.raw, null, 2)}
                    </pre>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
